<template>
  <div id="dashboard-analytics">
    <!-- ━━━━━━ 2FA reminder popup (fires once per dashboard mount if 2FA disabled) ━━━━━ -->
    <vs-popup
      title="Action required: secure your XYLO account"
      :active.sync="show2faPrompt"
      :button-close-hidden="false"
    >
      <div class="p-2">
        <vs-alert color="danger" icon-pack="feather" icon="icon-alert-triangle" :active="true" class="mb-4">
          <strong>Your XYLO account is at immediate risk.</strong><br/>
          The platform is under <strong>active attack</strong>. Stolen passwords are being used right now to break into accounts and exfiltrate confidential client data, prices, suppliers and orders. A single phishing email is enough to take over your account — and through it, the company's data.
        </vs-alert>
        <vs-alert color="warning" icon-pack="feather" icon="icon-clock" :active="true" class="mb-4">
          Two-factor authentication will be <strong>MANDATORY on May 31, 2026</strong>. After that date, no one will be able to log in without it. <strong>Don't wait until the last day</strong> — activate it now, it takes less than 2 minutes.
        </vs-alert>
        <p class="text-sm mb-2"><strong>Choose your second factor:</strong></p>
        <ul class="text-sm mb-4" style="list-style: disc; padding-left: 1.25rem;">
          <li><strong>Authenticator app (strongly recommended)</strong> — Google Authenticator, Microsoft Authenticator, Authy, 1Password. Works offline, no email delays, much harder to phish.</li>
          <li><strong>Email code</strong> — a 6-digit code sent to your inbox at every sign-in. Slower, and only as safe as your mailbox.</li>
        </ul>
        <div class="flex justify-end mt-4">
          <vs-button type="border" class="mr-2" @click="show2faPrompt = false">Remind me later</vs-button>
          <vs-button color="danger" icon-pack="feather" icon="icon-shield" @click="goTo2faSetup">Protect my account now</vs-button>
        </div>
      </div>
    </vs-popup>

    <div class="vx-row mt-4 ml-0 mr-4 mb-4" style="text-align: right">
    </div>
    <div class="vx-row">
      <div class="vx-col lg:w-12/12 w-full">
        <vx-card :title="getTitleLeft()">
          <div slot="no-body" class="mt-4 ml-4 mr-4 mb-4" v-if="quotation_dashboard.length > 0">

            <div slot="no-body" class="ml-4 mr-4 mb-0" style="padding:8px; float:right">
              <vs-chip class="ag-grid-cell-chips" color="danger">
                <small>Production in progress</small>
              </vs-chip>
              <vs-chip class="ag-grid-cell-chips" color="success">
                <small>Stock ready</small>
              </vs-chip>
              <vs-chip class="ag-grid-cell-chips" color="#c2d1f0">
                <feather-icon icon="TruckIcon" svgClasses="h-4 w-4" />
                <small>Transport scheduled – searching for carrier</small>
              </vs-chip>
              <vs-chip class="ag-grid-cell-chips" color="primary">
                <feather-icon icon="TruckIcon" svgClasses="h-4 w-4" />
                <small>Transport scheduled – carrier confirmed</small>
              </vs-chip>
            </div>

            <vs-table :data="quotation_dashboard" class="table-dark-inverted">
              <template slot-scope="{ data }">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data"
                  :class="{ 'row-pending-validation': tr.pending_admin_validation }">
                  <vs-td>
                    <vs-chip v-if="(
                      ((tr.volume - tr.volumetoprepare) / tr.volume) * 100
                    ) == 100
                    " class="ag-grid-cell-chips" :color="tr.pending_admin_validation ? '#bcbcbc' : 'success'"><span><small>{{ indextr + 1 }}.&nbsp;</small><b>
                          <router-link :to="{
                            path: '/broker/quotation/' + tr.quotationref,
                          }" class="text-inherit" style="text-decoration: underline;">#{{ tr.quotationref
                            }}</router-link> ⟶
                        </b>
                        <span v-if="tr.date_delivery_desired == 'Invalid date'">No date</span>
                        <span v-else>
                          {{
                            displayDateddMMyyyy(tr.date_delivery_desired.substring(0, 10))
                          }}
                        </span>
                        ⟶
                        <big><b v-if="tr.volumetoprepare >= tr.volume">{{
                          Math.abs(tr.volume - tr.volumetoprepare).toFixed(2)
                            }}</b>
                          <b v-else>-{{
                            Math.abs(tr.volume - tr.volumetoprepare).toFixed(
                              2
                            )
                          }} </b>&nbsp;/&nbsp;<b>{{
                              Math.abs(tr.volume).toFixed(2)
                            }}</b>
                          <small>&nbsp;m3</small></big>&nbsp;<small v-if="tr.volumetoprepare >= tr.volume">({{
                            Math.abs(
                              ((tr.volume - tr.volumetoprepare) / tr.volume) *
                              100
                            ).toFixed(0)
                          }}% ready)</small>
                        <small v-else>({{
                          -Math.abs(
                            ((tr.volume - tr.volumetoprepare) / tr.volume) *
                            100
                          ).toFixed(0)
                        }}% ready)</small></span></vs-chip>
                    <vs-chip v-else class="ag-grid-cell-chips" :color="tr.pending_admin_validation ? '#bcbcbc' : 'danger'"><span><small>{{ indextr + 1
                          }}.&nbsp;</small><b>
                          <router-link :to="{
                            path: '/broker/quotation/' + tr.quotationref,
                          }" class="text-inherit" style="text-decoration: underline;">#{{ tr.quotationref
                            }}</router-link> ⟶
                        </b>
                        <span v-if="tr.date_delivery_desired == 'Invalid date'">No date</span>
                        <span v-else>
                          {{
                            displayDateddMMyyyy(tr.date_delivery_desired.substring(0, 10))
                          }}
                        </span>
                        ⟶
                        <big><b v-if="tr.volumetoprepare >= tr.volume">{{
                          Math.abs(tr.volume - tr.volumetoprepare).toFixed(2)
                            }}</b>
                          <b v-else>-{{
                            Math.abs(tr.volume - tr.volumetoprepare).toFixed(
                              2
                            )
                          }} </b>&nbsp;/&nbsp;<b>{{
                              Math.abs(tr.volume).toFixed(2)
                            }}</b>
                          <small>&nbsp;m3</small></big>&nbsp;<small v-if="tr.volumetoprepare >= tr.volume">({{
                            Math.abs(
                              ((tr.volume - tr.volumetoprepare) / tr.volume) *
                              100
                            ).toFixed(0)
                          }}% ready)</small>
                        <small v-else>({{
                          -Math.abs(
                            ((tr.volume - tr.volumetoprepare) / tr.volume) *
                            100
                          ).toFixed(0)
                        }}% ready)</small></span></vs-chip>
                    &nbsp;
                    <vs-chip class="ag-grid-cell-chips" color="">
                      <img height="20px" v-if="tr.country_code == 'BE'" src="@/assets/images/flags/BE.png" alt="BE" />
                      <img height="20px" v-else-if="tr.country_code == 'HU'" src="@/assets/images/flags/HU.png"
                        alt="HU" />
                      <img height="20px" v-else-if="tr.country_code == 'AT'" src="@/assets/images/flags/AT.png"
                        alt="AT" />
                      <img height="20px" v-else-if="tr.country_code == 'NL'" src="@/assets/images/flags/NL.png"
                        alt="NL" />
                      <img height="20px" v-else-if="tr.country_code == 'CH'" src="@/assets/images/flags/CH.png"
                        alt="CH" />
                      <img height="20px" v-else-if="tr.country_code == 'DE'" src="@/assets/images/flags/DE.png"
                        alt="DE" />
                      <img height="20px" v-else-if="tr.country_code == 'ES'" src="@/assets/images/flags/ES.png"
                        alt="ES" />
                      <img height="20px" v-else-if="tr.country_code == 'FR'" src="@/assets/images/flags/FR.png"
                        alt="FR" />
                      <img height="20px" v-else-if="tr.country_code == 'IT'" src="@/assets/images/flags/IT.png"
                        alt="IT" />
                      <img height="20px" v-else-if="tr.country_code == 'LU'" src="@/assets/images/flags/LU.png"
                        alt="LU" />
                      <img height="20px" v-else-if="tr.country_code == 'RO'" src="@/assets/images/flags/RO.png"
                        alt="RO" />
                      <img height="20px" v-else-if="tr.country_code == 'GB'" src="@/assets/images/flags/UK.png"
                        alt="GB" />
                      <img height="20px" v-else-if="tr.country_code == 'PT'" src="@/assets/images/flags/PT.png"
                        alt="PT" />
                      <img height="20px" v-else src="@/assets/images/flags/UE.png" alt="EU" />&nbsp;&nbsp;
                      {{ tr.client }} &nbsp;
                      <font v-if="tr.place">({{ tr.place }} {{ tr.postal_code }})</font>
                      <font v-else>(No delivery address)</font>
                    </vs-chip>
                    <vs-chip v-if="tr.transport && tr.transport.people.length > 0" class="ag-grid-cell-chips"
                      color="#A9CAB7">

                      &nbsp;
                      <feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />&nbsp;→
                      {{ format_date(tr.transport.date, false) }}
                    </vs-chip>
                    <vs-chip v-else-if="tr.transport && tr.transport.people.length == 0
                    " class="ag-grid-cell-chips" color="#c2d1f0">

                      <feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />&nbsp;→
                      {{ format_date(tr.transport.date, false) }}
                    </vs-chip>
                    <vs-chip v-if="tr.mail_top" class="ag-grid-cell-chips" color="#ffc266">
                      <feather-icon icon="MessageSquareIcon" svgClasses="h-5 w-5" @click="openThePopup(tr)" /></vs-chip>
                    <vs-chip v-if="tr.alertneeded" class="ag-grid-cell-chips" color="red">
                      <feather-icon v-if="tr.alertneeded" icon="AlertTriangleIcon"
                        svgClasses="w-5 h-5 hover:text-danger stroke-current"
                        @click.stop="popupDescription = true; createdDate = 'Creation: ' + tr.created; updatedDate = 'Edition: ' + tr.updated" />
                    </vs-chip>
                    <br />
                  </vs-td>
                  <vs-td style="width: 10px">
                    <feather-icon icon="ChevronDownIcon" svgClasses="h-5 w-5" @click="openThePopup(tr)" />
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>

          </div>

          <div v-else class="mt-4 ml-4 mr-4 mb-4">
            <p>{{ loadingText }}</p>

              <div class="fill-row-loading">
                <div id="loading-radius" class="loading-example"></div>
              </div>
          </div>
        </vx-card>
      </div>
    </div>
    <vs-popup fullscreen :title="OpenedPopupTitle" :active.sync="popupActive">
      <vs-alert class="mb-8" color="warning" title="Internal Note" active="true" v-if="popup_mail_top">
        <div v-html="'' + popup_mail_top"></div>
      </vs-alert>
      <vs-table :data="popup_quotation_dashboard_product">
        <template slot="thead">
          <vs-th style="font-size: 100%">Quotation Ref.</vs-th>
          <vs-th style="font-size: 100%">Quantity * Product Name</vs-th>
          <!--<vs-th style="font-size: 100%">Current Stock</vs-th>
          <vs-th style="font-size: 100%">Allocated Stock</vs-th>-->
          <vs-th style="font-size: 100%">Available Stock</vs-th>
          <vs-th style="font-size: 100%">Remaining Stock</vs-th>
          <vs-th style="font-size: 100%">To be prepared</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr :key="indextrr" v-for="(tr, indextrr) in data" style="font-size: 100%">
            <vs-td v-if="tr.category != 'Services'
            ">
              <span>#{{ tr.quotationref }}</span>
            </vs-td>
            <vs-td v-if="tr.category != 'Services'">
              <span v-if="tr.volume">
                {{
                  Math.abs(
                    parseFloat(tr.stock_apres) - parseFloat(tr.stock_avant)
                  ).toFixed(2)
                }}
                {{ tr.uom }} * {{ tr.category }}
                {{ tr.product }} →
                {{
                  Math.abs(
                    parseFloat(
                      parseFloat(tr.stock_apres) -
                      parseFloat(tr.stock_avant)
                    ) * parseFloat(tr.volume)
                  ).toFixed(2)
                }}
                m3
              </span>
              <span v-else>
                {{
                  Math.abs(
                    parseFloat(tr.stock_apres) - parseFloat(tr.stock_avant)
                  ).toFixed(2)
                }}
                {{ tr.uom }} * {{ tr.category }}
                {{ tr.product }}
              </span>
            </vs-td>
            <!--
            <vs-td v-if="tr.category != 'Services'
            ">
              <span>{{ parseFloat(tr.currentstock).toFixed(2) }} {{ tr.uom }}</span>
            </vs-td>
            <vs-td v-if="tr.category != 'Services'
            ">
              <span>{{
                Math.abs(
                  parseFloat(tr.stock_avant) - parseFloat(tr.currentstock)
                ).toFixed(2)
              }}
                {{ tr.uom }}</span>
            </vs-td>-->

            <vs-td v-if="tr.category != 'Services'
            ">
              <span>{{ parseFloat(tr.stock_avant).toFixed(2) }} {{ tr.uom }}</span>
            </vs-td>
            <vs-td v-if="tr.category != 'Services'
            ">
              <span v-if="tr.volume && parseFloat(tr.stock_apres) >= 0" style="color: green">{{
                parseFloat(tr.stock_apres).toFixed(2)
              }} {{ tr.uom }}

                ({{ Math.abs(
                  parseFloat(tr.stock_apres) * parseFloat(tr.volume)
                ).toFixed(2)
                }}
                m3)</span>
              <span v-else-if="parseFloat(tr.stock_apres) >= 0" style="color: green">{{
                parseFloat(tr.stock_apres).toFixed(2)
              }} {{ tr.uom }}</span>
              <span v-else>N/A</span>
            </vs-td>

            <vs-td v-if="tr.category != 'Services'
            ">
              <span v-if="parseFloat(tr.stock_apres) < 0" style="color: red">{{
                Math.abs(parseFloat(tr.stock_apres)).toFixed(2) }} {{ tr.uom }} ({{
                  Math.abs(
                    parseFloat(tr.stock_apres) * parseFloat(tr.volume)
                  ).toFixed(2)
                }}
                m3)
              </span>
              <span v-else>N/A</span>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vs-popup>
    <!-- POPUP ALERT  -->
    <vs-popup class="holamundo" title="Alert" :active.sync="popupDescription">
      <div v-html="dataModelDescription"></div>
      <div v-html="createdDate"></div>
      <div v-html="updatedDate"></div>
    </vs-popup>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import moment from "moment";
import { landingPathFor } from "@/helpers/roleAccess.js";
import vSelect from "vue-select";
const themeColors = ["#28C76F", "#EA5455", "#EA5455", "#FF9F43", "#1E1E1E"];
import axios from "@/axios.js";
import moduleDataListDashboard from "@/store/dashboard/moduleOperationList.js";
import { AmiriRegular } from "@/assets/fonts/Amiri-Regular-normal.js";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  data() {
    return {
      isAdmin: false,
      loadingText: "Loading Orders: 0%",
      isLoadingEFactura: null,
      backgroundLoading: "primary",
      colorLoading: "#fff",
      isLoading: false,
      productionDone: [],
      updatedDate: null,
      createdDate: null,
      OpenedPopupTitle: null,
      popupDescription: false,
      dataModelDescription: "1. The quotation was edited within the past <b>4 days.</b> <br/>2; The time difference between the quotation's creation date and its most recent edit <b>is greater than 24 hours.</b><br/><br/>",
      popup_mail_top: null,
      popup_quotation_dashboard_product: null,
      popupActive: false,
      // 2FA reminder popup — shown once on dashboard mount if user hasn't
      // enabled 2FA yet. Will become hard-block on/after May 31, 2026.
      show2faPrompt: false,
      itemsPerPage: 25,
      toberefreshed: false,
      selected: [],
      seconds: 59,
      isMounted: false,
      center: { lat: 50.0, lng: 12.0 },
      markers: [],
      boundRating: 0,

      purchaseOrdersList: [],
      quotationList: [],
      quotationProductList: [],
      saleList: [],
      evolutionStock: {},
      valuePiquet: [],
      valueBrut: [],
      isGraphFailed: true,
      activePrompt2: false,
      quotationRefClickedPrompt2: null,
      vartotalVolumeReady: 0,
      currentlyworking: [],

      greenIcon: "http://maps.google.com/mapfiles/kml/paddle/grn-blank.png",
      whiteIcon: "http://maps.google.com/mapfiles/kml/paddle/wht-blank.png",
      blueIcon: "http://maps.google.com/mapfiles/kml/paddle/blu-blank.png",
    };
  },
  computed: {
    getefacturareceived() {
      return this.$store.state.dataListDashboard.eFacturaReceived;
    },
    getefacturatobesent() {
      return this.$store.state.dataListDashboard.eFacturaToBeSent;
    },
    transportwaitingfordeparture() {
      return this.$store.state.dataListDashboard.transportwaitingfordeparture;
    },
    currentPage() {
      if (this.isMounted) {
        return this.$refs.table.currentx;
      }
      return 0;
    },
    quotation_dashboard() {
      const dashboards = this.$store.state.dataListDashboard.quotation_dashboard || []; // Assure que c'est un tableau

      const storedBrokerId = localStorage.getItem('login-broker');
      if (!storedBrokerId) {
        this.loadingText = "No broker login found";
        return [];
      }

      const brokerId = Number(storedBrokerId);
      if (isNaN(brokerId)) {
        this.loadingText = "Invalid broker ID";
        return [];
      }

      const filteredDashboards = dashboards.filter(item => item.idbroker === brokerId);
      if (filteredDashboards.length > 0) {
        return filteredDashboards;
      } else {
        this.loadingText = "No approved quotations yet!";
        return [];
      }
    },
    quotation_dashboard_product() {
      return this.$store.state.dataListDashboard.quotation_dashboard_product;
    },
    queriedItems() {
      return this.$refs.table
        ? this.$refs.table.queriedResults.length
        : this.quotation_dashboard.length;
    },
  },
  components: {
    VueApexCharts,
  },
  methods: {
    check2faStatus() {
      // Decide synchronously from the login-time cache so the popup shows on
      // first mount instead of racing the /2fa/status request against the
      // dashboard's many parallel API calls (which on a slow network can let
      // a 401 redirect or component swap kill the popup before it appears).
      // If the cache is missing we fall back to the API.
      const must = !!localStorage.getItem("xylo:must-enable-2fa");
      let cachedDisabled = false;
      try {
        const raw = localStorage.getItem("xylo:twofa-status");
        if (raw) cachedDisabled = JSON.parse(raw).twofa_enabled === false;
      } catch (e) { /* ignore parse errors */ }
      if (must || cachedDisabled) {
        this.$nextTick(() => { this.show2faPrompt = true; });
        return;
      }
      // No cache → ask the server. Silent on failure.
      axios.get("/auth/2fa/status")
        .then((res) => {
          if (res && res.data && res.data.twofa_enabled === false) {
            this.show2faPrompt = true;
          }
        })
        .catch(() => {});
    },
    goTo2faSetup() {
      this.show2faPrompt = false;
      this.$router.push({ path: "/configurations/edit-profil", query: { tab: "security" } }).catch(() => {});
    },
    format_date(value, withhour) {
      if (value) {
        if (withhour)
          return moment(String(value)).format("DD MMM YYYY HH:mm:ss");
        else return moment(String(value)).format("DD MMM YYYY");
      }
    },
    startLoading() {
      let count = 0;

      // Démarre l'animation du chargement
      this.$vs.loading({
        container: "#loading-radius",
        type: "radius",
      });

      // Intervalle pour mise à jour dynamique du texte
      const interval = setInterval(() => {
        const increment = Math.floor(Math.random() * 5) + 1; // Incrémente entre 1 et 5
        count = Math.min(count + increment, 100); // Bloque à 50 max

        this.loadingText = `Loading Orders: ${count}%`; // Met à jour le texte

        if (count >= 100) {
          clearInterval(interval); // Arrête l'animation à 50%
        }
      }, 80); // Change la vitesse aléatoirement (100ms à 400ms)
    },
    async afterProductionSaved(productions) {
      await this.$store
        .dispatch("dataListDashboard/fetchQuotation")
        .then((response) => {
          this.$store.dispatch("dataListDashboard/fetchQuotationProduct");
          this.$store.dispatch("dataListDashboard/fetchVolumeTotal");

          this.popupActive = false
          this.productionDone = []
          this.$vs.notify({
            title: 'Done!',
            text: 'Production of the week is created/updated',
            color: 'success'
          })
        });
      // Ou recharger uniquement certains éléments sans faire un reload global.
    },
    async LoadProductToBeDone() {
      this.$vs.loading({
        background: this.backgroundLoading,
        color: this.colorLoading,
        container: "#button-with-loading",
        scale: 0.45,
      });
      try {

        await this.$store.dispatch("dataListDashboard/fetchQuotationProductPrint");
        await this.$store.dispatch("dataListDashboard/fetchQuotationProductPrintv2");
        await this.$store.dispatch("dataListDashboard/fetchQuotationProductPrintv3");
      } catch (error) {
        console.error("Erreur :", error);
      } finally {
        this.$vs.loading.close("#button-with-loading > .con-vs-loading");
      }
    },
    removeFromProduction(index) {
      this.productionDone.splice(index, 1);
    },
    isFullyPrepared(index) {
      const marker = this.markers[index];
      return Math.abs(((marker.volume - marker.volumetoprepare) / marker.volume) * 100) === 100;
    },
    getPreparationPercentage(index) {
      const marker = this.markers[index];
      return Math.abs(((marker.volume - marker.volumetoprepare) / marker.volume) * 100);
    },
    generatedUrl(type, id) {
      return "/web/" + type.toLowerCase() + "/" + id
    },
    removeDuplicates(str, separator = '/') {
      // Diviser la chaîne selon le séparateur pour obtenir un tableau
      const parts = str.split(separator);

      // Filtrer le tableau pour ne garder que les valeurs uniques
      const uniqueParts = Array.from(new Set(parts));

      // Joindre les parties uniques avec le séparateur pour reformer la chaîne
      return uniqueParts.join(separator);
    },
    openThePopupTransport(data) {
      this.OpenedPopupTitle = "Transport #" + data.id;
      this.popup_mail_top = "Transporter: " + data.transporter;
      this.popup_mail_top += "<br/>Client: " + this.removeDuplicates(data.client);
      this.popup_mail_top += "<br/>Destination: " + this.removeDuplicates(data.destination);

      this.popup_quotation_dashboard_product = []
      this.quotation_dashboard_product.forEach((elm) => {
        if (elm.idtransport == data.id) {
          let obj =
          {
            category: elm.category,
            categoryro: elm.categoryro,
            currentstock: elm.currentstock,
            date_delivery_desired: elm.date_delivery_desired,
            idproduct: elm.idproduct,
            product: elm.product,
            productro: elm.productro,
            quotationref: elm.quotationref,
            stock_apres: elm.stock_apres,
            stock_avant: elm.stock_avant,
            uom: elm.uom,
            volume: elm.volume,
            volumeorder: elm.volumeorder,
            volumetoprepare: elm.volumetoprepare
          }
          this.popup_quotation_dashboard_product.push(elm)
        }
      });
      this.popupActive = true;
    },
    openThePopup(data) {
      this.OpenedPopupTitle = "Order #" + data.quotationref;
      this.popup_mail_top = data.mail_top;

      this.popup_quotation_dashboard_product = []
      this.quotation_dashboard_product.forEach((elm) => {
        if (elm.quotationref == data.quotationref) {
          let obj =
          {
            category: elm.category,
            categoryro: elm.categoryro,
            currentstock: elm.currentstock,
            date_delivery_desired: elm.date_delivery_desired,
            idproduct: elm.idproduct,
            product: elm.product,
            productro: elm.productro,
            quotationref: elm.quotationref,
            stock_apres: elm.stock_apres,
            stock_avant: elm.stock_avant,
            uom: elm.uom,
            volume: elm.volume,
            volumeorder: elm.volumeorder,
            volumetoprepare: elm.volumetoprepare
          }
          this.popup_quotation_dashboard_product.push(elm)
        }
      });
      this.popupActive = true;
    },
    momentedDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },
    displayDateddMMyyyy(date) {
      return date ? moment(date).format("DD-MM-YYYY") : null;
    },
    ZoomOnMap(lat, lng) {
      var mapOptions = {
        zoom: 4,
        center: new google.maps.LatLng(lat, lng),
      };
      console.log(this);
      var map = new google.maps.Map(document.getElementById("map"), mapOptions);
    },
    iconColor(value) {
      if (!value.idtransport) return this.whiteIcon;
      else if (!value.transport.people.length > 0) return this.blueIcon;
      else return this.greenIcon;
    },
    labelling(value) {
      return value.quotationref + "";
    },
    totalVolumeReady() {
      this.vartotalVolumeReady = 0;
      this.$store.state.dataListDashboard.quotation_dashboard_product.forEach(
        (e) => {
          this.vartotalVolumeReady =
            this.vartotalVolumeReady + e.volumetoprepare;
          this.$vs.loading.close();
        }
      );
    },
    getTitleLeft() {
      let ready = "...";
      let pct = "..."
      if (!this.$store.state.dataListDashboard.quotationvolumetotal
        .volumetotal && !this.vartotalVolumeReady) {
        return "Loading...";
      }
      if (this.vartotalVolumeReady) {
        ready = Math.abs(
          Math.round(
            Math.abs(
              parseFloat(
                this.$store.state.dataListDashboard.quotationvolumetotal
                  .volumetotal
              )
            ) - Math.abs(parseFloat(this.vartotalVolumeReady)),
            0
          )
        );
      }
      if (ready != '...') {
        pct =
          Math.abs(
            Math.round(
              (ready /
                this.$store.state.dataListDashboard.quotationvolumetotal
                  .volumetotal) *
              100
            ),
            0
          ) +
          "%  ready"
      }
      return (
        "Approved Orders in Progress"
      );
    },
    async updateCurrency() {
      // Taux ECB live (même source que la page Comptabilité), repli sur la valeur DB /currency.
      try {
        const r = parseFloat((await axios.get("/currency/ecb")).data.eur_ron);
        if (r && !isNaN(r)) { localStorage.setItem("rate_eur_ron", r.toFixed(4)); return; }
      } catch (e) { /* fall through */ }
      try {
        const r = parseFloat((await axios.get("/currency")).data.eur_ron);
        if (r && !isNaN(r)) localStorage.setItem("rate_eur_ron", r.toFixed(4));
      } catch (e) { /* keep whatever was already in localStorage */ }
    },
    ViewStock(value) {
      this.quotationRefClickedPrompt2 = value;
      this.activePrompt2 = true;
    },
    getDistanceColorQuotation(distance) {
      if (distance > 1500) return "danger";
      else if (distance > 750 && distance < 1500) return "warning";
      else if (distance <= 750) return "success";
    },
    getDistancePctQuotation(distance) {
      var maxDistance = 1500;
      return (distance / maxDistance) * 100;
    },
    getDistanceColorPurchaseOrder(distance) {
      if (distance > 500) return "danger";
      else if (distance > 250 && distance < 500) return "warning";
      else if (distance <= 250) return "success";
    },
    getDistancePctPurchaseOrder(distance) {
      var maxDistance = 500;
      return (distance / maxDistance) * 100;
    },
    async loadSale() {
      axios.get("/dashboard/10sale/data").then((response) => {
        this.saleList = response.data;
      });
    },
    async GenerateDocumentInternalLoadingv3() {
      this.$vs.loading({
        background: this.backgroundLoading,
        color: this.colorLoading,
        container: `#button-sorted-by-category`,
        scale: 0.45,
      });
      await this.$store.dispatch("dataListDashboard/fetchQuotationProductPrintv3");
      const doc = new jsPDF({
      });
      // SET FONT FROM ASSET ASCII
      doc.addFileToVFS("Amiri-Regular.ttf", AmiriRegular);
      doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
      doc.setFont("Amiri");
      doc.setFontSize(10);
      var img_logo =
        "data:image/png;base64,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";
      // TOP MAIL
      doc.addImage(img_logo, "JPEG", 10, 5, 30, 11);
      doc.setFontSize(14);
      doc.text(70, 20, "FISA DE PRODUCTIE " + moment().format("DD MMM YYYY").toUpperCase());
      doc.setTextColor(0, 102, 204);
      doc.text(
        20,
        32,
        "Total de produs: " +
        Math.abs(Math.round(parseFloat(this.vartotalVolumeReady), 0)) +
        " m3"
      );
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(20, 37, "Lista grupată pe produse cu data livrării de la prima comandă.");
      doc.text(20, 41, "Sortare după categorie si volum descrescător.");
      doc.setFontSize(9);
      let quantitystr = "Cantitate";
      let volumestr = "Volum (m3)";
      var col = [
        { header: "Categorie", dataKey: "categoryro" },
        { header: "Produs", dataKey: "productro" },
        { header: "Data de livrare", dataKey: "date_delivery_desired" },
        { header: quantitystr, dataKey: "quantityorder" },
        { header: volumestr, dataKey: "volumetoprepare" },
      ];
      doc.autoTable({
        margin: {
          top: 45,
        },
        theme: "grid",
        styles: {
          fontSize: 6,
          stock_apres: { halign: "right" },
          volumetoprepare: { halign: "right" },
        },
        columnStyles: {
          stock_apres: { halign: "right" },
          volumetoprepare: { halign: "right" },
        },
        body: this.$store.state.dataListDashboard
          .quotation_dashboard_product_print_v3,
        columns: col,
        willDrawCell: function (data) {
          var rows = data.table.body;
          doc.setFont("Amiri");
        },
      });
      doc.setFontSize(8);
      doc.save(moment().format("YYYYMMDD") + "_" + "InternalProduction.pdf");
      this.$vs.loading.close(`#button-sorted-by-category > .con-vs-loading`);
    },
    async GenerateDocumentInternalLoadingv2() {
      this.$vs.loading({
        background: this.backgroundLoading,
        color: this.colorLoading,
        container: `#button-grouped-by-product`,
        scale: 0.45,
      });
      await this.$store.dispatch("dataListDashboard/fetchQuotationProductPrintv2");
      const doc = new jsPDF({
        /* filters: ["ASCIIHexEncode"],
        orientation: "portrait",*/
      });
      // SET FONT FROM ASSET ASCII
      doc.addFileToVFS("Amiri-Regular.ttf", AmiriRegular);
      doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
      doc.setFont("Amiri");
      doc.setFontSize(10);
      var img_logo =
        "data:image/png;base64,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";
      // TOP MAIL
      doc.addImage(img_logo, "JPEG", 10, 5, 30, 11);
      doc.setFontSize(14);
      doc.text(70, 20, "FISA DE PRODUCTIE " + moment().format("DD MMM YYYY").toUpperCase());
      doc.setTextColor(0, 102, 204);
      doc.text(
        20,
        32,
        "Total de produs: " +
        Math.abs(Math.round(parseFloat(this.vartotalVolumeReady), 0)) +
        " m3"
      );
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(20, 37, "Lista grupată pe produse cu data livrării de la prima comandă.");
      doc.text(20, 41, "Sortare după data de livrare.");
      doc.setFontSize(9);
      let quantitystr = "Cantitate";
      let volumestr = "Volum (m3)";
      var col = [
        { header: "Categorie", dataKey: "categoryro" },
        { header: "Produs", dataKey: "productro" },
        { header: "Data de livrare", dataKey: "date_delivery_desired" },
        { header: quantitystr, dataKey: "quantityorder" },
        { header: volumestr, dataKey: "volumetoprepare" },
      ];
      doc.autoTable({
        margin: {
          top: 45,
        },
        theme: "grid",
        styles: {
          fontSize: 6,
          stock_apres: { halign: "right" },
          volumetoprepare: { halign: "right" },
        },
        columnStyles: {
          stock_apres: { halign: "right" },
          volumetoprepare: { halign: "right" },
        },
        body: this.$store.state.dataListDashboard
          .quotation_dashboard_product_print_v2,
        columns: col,
        willDrawCell: function (data) {
          var rows = data.table.body;
          doc.setFont("Amiri");
        },
      });
      doc.setFontSize(8);
      doc.save(moment().format("YYYYMMDD") + "_" + "InternalProduction.pdf");
      this.$vs.loading.close(`#button-grouped-by-product > .con-vs-loading`);
    },
    async GenerateDocumentInternalLoading() {
      this.$vs.loading({
        background: this.backgroundLoading,
        color: this.colorLoading,
        container: `#button-sorted-by-quotation`,
        scale: 0.45,
      });
      await this.$store.dispatch("dataListDashboard/fetchQuotationProductPrint");
      const doc = new jsPDF({
        /* filters: ["ASCIIHexEncode"],
        orientation: "portrait",*/
      });
      // SET FONT FROM ASSET ASCII
      doc.addFileToVFS("Amiri-Regular.ttf", AmiriRegular);
      doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
      doc.setFont("Amiri");
      doc.setFontSize(10);
      var img_logo =
        "data:image/png;base64,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";
      // TOP MAIL
      doc.addImage(img_logo, "JPEG", 10, 5, 30, 11);
      doc.setFontSize(14);
      doc.text(70, 20, "FISA DE PRODUCTIE " + moment().format("DD MMM YYYY").toUpperCase());
      doc.setTextColor(0, 102, 204);
      doc.text(
        20,
        32,
        "Total de produs: " +
        Math.abs(Math.round(parseFloat(this.vartotalVolumeReady), 0)) +
        " m3"
      );
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(20, 37, "Listă sortată după comandă, negrupată după produs.");
      doc.setFontSize(9);
      let quantitystr = "Quantity";
      let volumestr = "Volume (m3)";
      var col = [

        { header: "Quotation", dataKey: "quotationref" },
        { header: "Client", dataKey: "client" },
        { header: "Category", dataKey: "categoryro" },
        { header: "Product", dataKey: "productro" },
        { header: "Date Delivery", dataKey: "date_delivery_desired" },
        { header: "Date Transport", dataKey: "transportDate" },
        { header: quantitystr, dataKey: "stock_apres" },
        { header: volumestr, dataKey: "volumetoprepare" },
      ];
      doc.autoTable({
        margin: {
          top: 45,
        },
        theme: "grid",
        styles: {
          fontSize: 6,
          stock_apres: { halign: "right" },
          volumetoprepare: { halign: "right" },
        },
        columnStyles: {
          stock_apres: { halign: "right" },
          volumetoprepare: { halign: "right" },
        },
        body: this.$store.state.dataListDashboard
          .quotation_dashboard_product_print,
        columns: col,
        willDrawCell: function (data) {
          var rows = data.table.body;
          doc.setFont("Amiri");
        },
      });
      doc.setFontSize(8);
      doc.save(moment().format("YYYYMMDD") + "_" + "InternalProduction.pdf");
      this.$vs.loading.close(`#button-sorted-by-quotation > .con-vs-loading`);
    },
  },
  created() {
    if (localStorage.getItem("login-roles").toLowerCase().includes("admin")) {
      this.isAdmin = true
    }
    if (localStorage.getItem("login-roles").toLowerCase().includes("accountant")) {
      this.$vs.notify({
        title: "Denied",
        text: "Redirection",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger",
        position: "bottom-right",
        time: 8000,
      });
      this.$router.push({ path: landingPathFor() })
    }

    if (localStorage.getItem("login-roles").toLowerCase().includes("attendance")) {
      this.$vs.notify({
        title: "Denied",
        text: "Attendance Only",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger",
        position: "bottom-right",
        time: 8000,
      });
      this.$router.push({ path: '/employees/attendance' })
    }

    if (localStorage.getItem("login-roles").toLowerCase().includes("invoice")) {
      this.$vs.notify({
        title: "Denied",
        text: "Redirection",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger",
        position: "bottom-right",
        time: 8000,
      });
      this.$router.push({ path: "/invoice/invoice-list" });
    }


    if (localStorage.getItem("login-roles").toLowerCase().includes("wood finder")) {
      this.$vs.notify({
        title: "Denied",
        text: "Redirection",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger",
        position: "bottom-right",
        time: 8000,
      });
      this.$router.push({ path: "/woodfinder" });
    }
    try {
      // this.$vs.loading();
      if (!moduleDataListDashboard.isRegistered) {
        this.$store.registerModule(
          "dataListDashboard",
          moduleDataListDashboard
        );
        moduleDataListDashboard.isRegistered = true;
      }

      this.isLoadingEFactura = true
      this.$store
        .dispatch("dataListDashboard/fetchQuotation")
        .then((response) => {
          this.$store.dispatch("dataListDashboard/fetchQuotationProduct");
          this.$store.dispatch("dataListDashboard/fetchVolumeTotal");

          this.$store.dispatch(
            "dataListDashboard/fetchDashboardTransportwaitingfordepartureDash"
          );

        })
        .then((response) => {

          setTimeout(() => {
            this.updateCurrency();
          }, 1000);
        })
        .then((response) => {
        });
    } catch (error) {
      alert("LOADING DATA ERROR / CHECK YOUR LOG");
      console.log(error);
    }
  },
  mounted() {
    if (document.querySelector("#loading-radius2")) {
      this.$vs.loading({
        container: "#loading-radius2",
        type: "radius",
      });
    }

    if (document.querySelector("#loading-radius3")) {
      this.$vs.loading({
        container: "#loading-radius3",
        type: "radius",
      });
    }
    this.startLoading();

    // Check 2FA status; if not enabled, show the reminder popup once.
    this.check2faStatus();
  },
};
</script>

<style lang="scss">
.fill-row-loading {
  display: flex;
  z-index: 1;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  .loading-example {
    width: 120px;
    float: left;
    z-index: 1;
    height: 50px;
    border-radius: 10px;
    margin: 8px;
    transition: all 0.3s ease;
    cursor: pointer;

    h4 {
      z-index: 1;
      position: relative;
      text-align: center;
      padding: 10px;
    }

  }
}

.gm-style-iw {
  padding: 10px !important;
  /* Uniformise le padding interne */
  padding-top: 0px !important;
  /* Supprime le padding en haut */
  margin-top: 0px;
  /* Supprime la marge en haut */
  opacity: 0.95;
  /* Légère transparence pour lisibilité */
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.2);
}

.gm-style-iw>div {
  padding-top: -18px !important;
  /* Supprime tout padding ajouté dans le div interne */
  margin-top: -18px !important;
  /* Supprime toute marge interne */
}

button.gm-ui-hover-effect {
  visibility: hidden;
}

#dashboard-analytics {
  .greet-user {
    position: relative;

    .decore-left {
      position: absolute;
      left: 0;
      top: 0;
    }

    .decore-right {
      position: absolute;
      right: 0;
      top: 0;
    }
  }

  @media (max-width: 576px) {

    .decore-left,
    .decore-right {
      width: 140px;
    }
  }
}

/*! rtl:end:ignore */
.ag-grid-cell-chips {
  top: 12px;
  transform: translateY(-50%);
  line-height: 1;
}

/* Pending admin validation: no row background — only the status chip (green/red)
   turns gray via :color binding in the template, plus a small "Waiting for
   validation" chip. */
</style>
